/**
 * Nạp trang của feature theo hai chế độ (Module Federation — W11, TDD §9.1):
 * - cục bộ (không có VITE_MF_REMOTES, hoặc cụm không cấu hình): `import.meta.glob` → Vite code-split mỗi trang một chunk;
 * - liên bang: `loadRemote('kovix_<cụm>/<feature>/pages/<Page>')` qua @module-federation/runtime — remote được đăng ký một lần từ env.
 * Route guard (entitlement + capability) vẫn ở shell trước khi component được nạp, nên người dùng chỉ tải mã của module họ được cấp.
 */
import { lazyRouteComponent } from '@tanstack/react-router';
import { CLUSTERS, type Cluster, FEATURE_CLUSTER, remoteName } from '../../../../../federation/clusters';

type PageModule = Record<string, unknown>;
const local = import.meta.glob<PageModule>([
  '/src/features/*/pages/*.tsx',
  '/src/features/admin/components/ModulePermissions.tsx',
]);

let remotes: Map<Cluster, string> | null = null;
let registered = false;

function federatedRemotes(): Map<Cluster, string> {
  if (remotes) return remotes;
  remotes = new Map();
  const raw = import.meta.env.VITE_MF_REMOTES as string | undefined;
  if (raw) {
    try {
      const map = JSON.parse(raw) as Partial<Record<Cluster, string>>;
      for (const c of CLUSTERS) if (map[c]) remotes.set(c, map[c] as string);
    } catch (e) {
      console.error('VITE_MF_REMOTES không phải JSON hợp lệ — nạp cục bộ', e);
    }
  }
  return remotes;
}

async function loadRemoteModule(cluster: Cluster, exposed: string): Promise<PageModule> {
  const rt = await import('@module-federation/runtime');
  if (!registered) {
    rt.registerRemotes(
      Array.from(federatedRemotes()).map(([c, entry]) => ({ name: remoteName(c), entry, type: 'module' as const })),
    );
    registered = true;
  }
  const mod = await rt.loadRemote<PageModule>(`${remoteName(cluster)}/${exposed}`);
  if (!mod) throw new Error(`Không nạp được remote ${remoteName(cluster)}/${exposed}`);
  return mod;
}

/** `feature` = thư mục trong src/features; `rel` = 'pages/<Page>' hoặc 'components/ModulePermissions'. */
export async function loadFeature(feature: string, rel: string): Promise<PageModule> {
  const cluster = FEATURE_CLUSTER[feature];
  if (cluster && federatedRemotes().has(cluster)) {
    try {
      return await loadRemoteModule(cluster, `${feature}/${rel}`);
    } catch (e) {
      console.warn(`Remote ${cluster} lỗi — thử nạp cục bộ`, e);
    }
  }
  const key = `/src/features/${feature}/${rel}.tsx`;
  const importer = local[key];
  if (!importer)
    throw new Error(`Không có trang ${key} (feature "${feature}" chưa khai trong federation/clusters.ts?)`);
  return importer();
}

/** Dùng trong router thay cho lazyRouteComponent(() => import('@/features/<feature>/pages/<Page>'), '<Page>'). */
export function featurePage(feature: string, page: string, exportName: string = page) {
  // biome-ignore lint/suspicious/noExplicitAny: lazyRouteComponent cần kiểu module động; export được kiểm ở runtime
  return lazyRouteComponent(() => loadFeature(feature, `pages/${page}`) as Promise<any>, exportName);
}

/**
 * Như featurePage/featureComponent nhưng GIỮ kiểu props của export — dùng khi router truyền props vào trang (R8: `any` làm mất props → lỗi JSX).
 * `C` chỉ là kiểu (`typeof import('@/features/…').<Export>`) — không kéo mã trang vào bundle shell.
 */
export function featureModule<C extends (props: never) => unknown>(feature: string, rel: string, exportName: string) {
  return lazyRouteComponent(() => loadFeature(feature, rel) as Promise<Record<string, C>>, exportName);
}

export function featureComponent(feature: string, rel: string, exportName: string) {
  // biome-ignore lint/suspicious/noExplicitAny: như trên
  return lazyRouteComponent(() => loadFeature(feature, rel) as Promise<any>, exportName);
}
